import { useState, useEffect, useCallback } from 'react';
import { INITIAL_COLUMNS } from '../constants';

export function useColumns(saveStateRef) {
  const [columns, setColumns] = useState(INITIAL_COLUMNS);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [colMenuOpen, setColMenuOpen] = useState(null);
  const [resizingCol, setResizingCol] = useState(null);
  const [draggedColId, setDraggedColId] = useState(null);
  const [addColMenuOpen, setAddColMenuOpen] = useState(null);

  const save = saveStateRef;

  // Fechar menus de coluna ao clicar fora
  useEffect(() => {
    if (!colMenuOpen && !addColMenuOpen) return;
    const handleClickOutside = (e) => {
      if (
        e.target.closest('[data-col-menu]') ||
        e.target.closest('[data-add-col-menu]') ||
        e.target.closest('[data-col-trigger]') ||
        e.target.closest('[data-add-col-trigger]')
      ) {
        return;
      }
      setColMenuOpen(null);
      setAddColMenuOpen(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [colMenuOpen, addColMenuOpen]);

  // Resize de colunas via mouse
  useEffect(() => {
    if (!resizingCol) return;
    const handleMouseMove = (e) => {
      const diffX = e.clientX - resizingCol.startX;
      const newWidth = Math.max(60, resizingCol.startWidth + diffX);
      setColumns(cols => cols.map(c => c.id === resizingCol.id ? { ...c, width: newWidth } : c));
    };
    const handleMouseUp = () => {
      setResizingCol(null);
      setColumns(prev => { save.current({ columns: prev }); return prev; });
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingCol, save]);

  const handleColDragStart = useCallback((e, id) => {
    setDraggedColId(id);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleColDrop = useCallback((e, targetId) => {
    setDraggedColId(prevDragged => {
      if (!prevDragged || prevDragged === targetId) return null;
      setColumns(prev => {
        const draggedIdx = prev.findIndex(c => c.id === prevDragged);
        const targetIdx = prev.findIndex(c => c.id === targetId);
        const newCols = [...prev];
        const [removed] = newCols.splice(draggedIdx, 1);
        newCols.splice(targetIdx, 0, removed);
        save.current({ columns: newCols });
        return newCols;
      });
      return null;
    });
  }, [save]);

  const handleAddNewColumn = useCallback((type, title) => {
    const newId = `col_${Date.now()}`;
    const safeTitle = (title || '').trim() || 'Nova Coluna';
    setColumns(prev => {
      const newCols = [...prev, { id: newId, title: safeTitle, type, width: 140 }];
      save.current({ columns: newCols });
      return newCols;
    });
    setAddColMenuOpen(null);
  }, [save]);

  const handleAddColumnRight = useCallback((targetColId, type, title) => {
    const newId = `col_${Date.now()}`;
    const safeTitle = (title || '').trim() || 'Nova Coluna';
    setColumns(prev => {
      const targetIndex = prev.findIndex(col => col.id === targetColId);
      if (targetIndex < 0) return prev;
      const newCols = [...prev];
      newCols.splice(targetIndex + 1, 0, { id: newId, title: safeTitle, type, width: 140 });
      save.current({ columns: newCols });
      return newCols;
    });
    setColMenuOpen(null);
  }, [save]);

  const handleDuplicateColumn = useCallback((colId) => {
    setColumns(prev => {
      const sourceIndex = prev.findIndex(col => col.id === colId);
      if (sourceIndex < 0) return prev;
      const source = prev[sourceIndex];
      const duplicated = {
        ...source,
        id: `col_${Date.now()}`,
        title: `${source.title} (copia)`,
      };
      const newCols = [...prev];
      newCols.splice(sourceIndex + 1, 0, duplicated);
      save.current({ columns: newCols });
      return newCols;
    });
    setColMenuOpen(null);
  }, [save]);

  const handleChangeColumnType = useCallback((colId, newType) => {
    setColumns(prev => {
      const newCols = prev.map(col => col.id === colId ? { ...col, type: newType } : col);
      save.current({ columns: newCols });
      return newCols;
    });
    setColMenuOpen(null);
  }, [save]);

  const handleRenameColumn = useCallback((colId, newTitle) => {
    const safeTitle = (newTitle || '').trim();
    if (!safeTitle) return;
    setColumns(prev => {
      const newCols = prev.map(col => col.id === colId ? { ...col, title: safeTitle } : col);
      save.current({ columns: newCols });
      return newCols;
    });
    setColMenuOpen(null);
  }, [save]);

  const handleDeleteColumn = useCallback((colId) => {
    setColumns(prev => {
      const newCols = prev.filter(c => c.id !== colId);
      setHiddenColumns(hc => {
        const newHiddenCols = hc.filter(id => id !== colId);
        save.current({ columns: newCols, hiddenColumns: newHiddenCols });
        return newHiddenCols;
      });
      return newCols;
    });
    setColMenuOpen(null);
  }, [save]);

  const toggleColumnVisibility = useCallback((colId) => {
    setHiddenColumns(prev => {
      const isHidden = prev.includes(colId);
      const newHiddenCols = isHidden ? prev.filter(id => id !== colId) : [...prev, colId];
      save.current({ hiddenColumns: newHiddenCols });
      return newHiddenCols;
    });
  }, [save]);

  const showAllColumns = useCallback(() => {
    setHiddenColumns([]);
    save.current({ hiddenColumns: [] });
  }, [save]);

  const unhideColumn = useCallback((colId) => {
    setHiddenColumns(prev => {
      const newHiddenCols = prev.filter(id => id !== colId);
      save.current({ hiddenColumns: newHiddenCols });
      return newHiddenCols;
    });
  }, [save]);

  return {
    columns, setColumns,
    hiddenColumns, setHiddenColumns,
    colMenuOpen, setColMenuOpen,
    resizingCol, setResizingCol,
    draggedColId, setDraggedColId,
    addColMenuOpen, setAddColMenuOpen,
    handleColDragStart, handleColDrop,
    handleAddNewColumn, handleAddColumnRight, handleDuplicateColumn, handleChangeColumnType, handleRenameColumn, handleDeleteColumn,
    toggleColumnVisibility, showAllColumns, unhideColumn,
  };
}
